import { toRaw } from 'vue';

import ActivityUpdate from '@/models/ActivityUpdate.js';
import axios from '@/plugins/axios.js';

function base64PayloadToBlob(data, mimeType) {
    if (!data || typeof data !== 'string') {
        return null;
    }

    const base64 = data.includes(',') ? data.split(',')[1] : data;

    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
        }

        return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
    } catch {
        return null;
    }
}

/**
 * Resolve a File/Blob from a media item (Vue reactive proxies break instanceof File).
 */
export function getAttachmentFile(item) {
    const candidate = toRaw(item?.file) ?? item?.file;

    if (candidate instanceof Blob) {
        return candidate;
    }

    return base64PayloadToBlob(item?.data, item?.type);
}

export function announcementMediaHasUploadableFiles(media = []) {
    return media.some(item => getAttachmentFile(item) !== null);
}

export function buildAnnouncementFormData(payload, media = []) {
    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
        if (key === 'media' || value === null || value === undefined) {
            return;
        }

        if (typeof value === 'boolean') {
            formData.append(key, value ? '1' : '0');
            return;
        }

        formData.append(key, value);
    });

    media.forEach((item, index) => {
        formData.append(`media[${index}][name]`, item.name ?? '');

        const file = getAttachmentFile(item);

        if (file) {
            formData.append(`media[${index}][file]`, file, item.name ?? 'upload');
        }
    });

    return formData;
}

export function postCourseAnnouncement(courseId, payload, media = []) {
    const url = `/api/courses/${courseId}/announcements`;

    if (announcementMediaHasUploadableFiles(media)) {
        return axios.post(url, buildAnnouncementFormData(payload, media), {
            errorHandle: false,
        });
    }

    const { media: _media, ...jsonPayload } = payload;

    return axios.post(url, jsonPayload, { errorHandle: false });
}

/**
 * Activity updates go through the Vuex-ORM model rather than raw axios so the response is
 * persisted into the store the announcement list reads from.
 *
 * Unlike the course path this keeps `media` on the JSON body: the endpoint still accepts
 * base64 attachments through the media-library rules when no real File is staged.
 */
export function postActivityUpdate(payload, media = []) {
    const body = announcementMediaHasUploadableFiles(media) ? buildAnnouncementFormData(payload, media) : payload;

    return ActivityUpdate.api().post('/api/activity-updates', body, { errorHandle: false });
}
